/**
 * 전역 검색.
 *
 * 프로토타입에는 장식이었지만 실제로 동작하게 만든다. 운영자가 콘솔에서 가장
 * 자주 하는 일이 "그 화면 어디 있더라"이고, 목록마다 들어가서 거르는 것보다
 * 이름으로 바로 찾는 편이 빠르다.
 *
 * 자산과 화면 두 곳을 동시에 찾는다. 서버가 두 목록에 `q` 를 지원한다.
 *
 * **좁은 화면(768px 미만)에서는 돋보기 단추다.** 누르면 상단 바 바로 아래로 입력칸이
 * 펼쳐진다. 휴대폰 폭의 상단 바에 입력칸을 늘 두면 제목이 들어갈 자리가 없다 —
 * 예전에는 아예 숨겨서, 휴대폰으로는 검색할 길이 없었다.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Monitor, Search } from 'lucide-react';
import type { SignageOneClient } from '../../api/client.js';
import { useDebounced } from '../../hooks.js';

interface Hit {
  kind: 'asset' | 'display';
  id: string;
  label: string;
  detail: string;
}

export function GlobalSearch({ client }: { client: SignageOneClient }): ReactNode {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  /** 좁은 화면에서 입력칸을 펼쳤는가. 넓은 화면에서는 입력칸이 늘 있으므로 뜻이 없다. */
  const [expanded, setExpanded] = useState(false);
  const [hits, setHits] = useState<Hit[]>([]);
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const query = useDebounced(term.trim(), 220);

  // "/" 로 검색창에 바로 간다. 프로토타입이 그렇게 안내하고 있어 실제로 되게 한다.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (event.key === '/' && !typing) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 바깥을 누르면 닫는다.
  useEffect(() => {
    const onDown = (event: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // 펼치면 바로 입력할 수 있게 한다 — 돋보기를 누른 사람이 입력칸을 한 번 더 누르게 하지 않는다.
  useEffect(() => {
    if (expanded) inputRef.current?.focus();
  }, [expanded]);

  useEffect(() => {
    if (query.length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    setBusy(true);

    // 한쪽이 실패해도 다른 쪽 결과는 보여준다 — 검색이 통째로 죽는 것보다 낫다.
    void Promise.allSettled([
      client.listAssets({ q: query }),
      client.listDisplays({ q: query }),
    ]).then(([assets, displays]) => {
      if (cancelled) return;
      const next: Hit[] = [];
      if (assets.status === 'fulfilled') {
        for (const asset of assets.value.items.slice(0, 5)) {
          next.push({ kind: 'asset', id: asset.id, label: asset.title, detail: TYPE_LABEL[asset.type] ?? asset.type });
        }
      }
      if (displays.status === 'fulfilled') {
        for (const display of displays.value.items.slice(0, 5)) {
          next.push({
            kind: 'display',
            id: display.id,
            label: display.name,
            detail: display.online ? '온라인' : '오프라인',
          });
        }
      }
      setHits(next);
      setCursor(0);
      setBusy(false);
    });

    return () => {
      cancelled = true;
    };
  }, [client, query]);

  const go = (hit: Hit): void => {
    navigate(hit.kind === 'asset' ? `/content/${hit.id}` : `/displays/${hit.id}`);
    setOpen(false);
    setExpanded(false);
    setTerm('');
    inputRef.current?.blur();
  };

  const showPanel = open && term.trim().length >= 2;
  const empty = useMemo(() => showPanel && !busy && hits.length === 0, [showPanel, busy, hits.length]);

  return (
    <div ref={boxRef} className="md:w-full md:max-w-md">
      <button
        type="button"
        aria-label="콘텐츠와 화면 검색"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 md:hidden"
      >
        <Search className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </button>

      {/*
        좁은 화면에서는 상단 바 아래로 펼친다(상단 바가 `sticky` 라 여기의 기준이 된다).
        넓은 화면에서는 상단 바 가운데에 늘 놓인다.
      */}
      <div
        className={`${
          expanded ? 'absolute inset-x-0 top-full border-b border-slate-200 bg-white px-4 py-2 shadow-sm' : 'hidden'
        } md:static md:block md:border-0 md:bg-transparent md:p-0 md:shadow-none`}
      >
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            strokeWidth={2}
          />
          <input
            ref={inputRef}
            type="search"
            value={term}
            placeholder="콘텐츠, 화면 검색…"
            aria-label="콘텐츠와 화면 검색"
            className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-9 text-[13.5px] text-slate-700 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-th-4 focus:bg-white focus:ring-2 focus:ring-th-5/15"
            onChange={(event) => {
              setTerm(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setOpen(false);
                setExpanded(false);
                event.currentTarget.blur();
              } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                setCursor((n) => Math.min(n + 1, hits.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setCursor((n) => Math.max(n - 1, 0));
              } else if (event.key === 'Enter' && hits[cursor]) {
                go(hits[cursor]);
              }
            }}
          />
          {term === '' && (
            <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-[11.5px] font-medium text-slate-400 lg:inline-block">
              /
            </kbd>
          )}

          {showPanel && (
            <div className="absolute left-0 right-0 top-11 z-50 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
              {busy && hits.length === 0 ? (
                <p className="px-4 py-3 text-[13.5px] text-slate-400">찾는 중…</p>
              ) : null}
              {empty ? <p className="px-4 py-3 text-[13.5px] text-slate-400">일치하는 항목이 없습니다</p> : null}
              <ul>
                {hits.map((hit, index) => {
                  const Icon = hit.kind === 'asset' ? FileText : Monitor;
                  return (
                    <li key={`${hit.kind}:${hit.id}`}>
                      <button
                        type="button"
                        onMouseEnter={() => setCursor(index)}
                        onClick={() => go(hit)}
                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          index === cursor ? 'bg-slate-50' : ''
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.9} />
                        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-slate-700">
                          {hit.label}
                        </span>
                        <span className="shrink-0 text-[11.5px] text-slate-400">{hit.detail}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TYPE_LABEL: Record<string, string> = {
  image: '이미지',
  video: '영상',
  web: '웹',
  template: '템플릿',
  html: 'HTML',
};
